import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import { InputAdornment } from "@mui/material";
import { FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { LARGO, mensajeLargo } from "consts/limites-campos";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/ModalActions.module.scss";
import { LEYES } from "consts/leyes";
import { RNHConvenioUpd } from "services/services-mia/programa-salud/personal/RNHConvenioUpd";
import { RNHConvenioGet } from "services/services-mia/programa-salud/personal/RNHConvenioGet";

/**
 * Modificación de un convenio: los mismos tres campos del alta.
 *
 * La precarga sale de la fila seleccionada y no de un Get por id, porque
 * RNHConvenioGet no filtra por id. Por eso la fila también trae `version`, que es lo
 * que el Upd necesita para el control de concurrencia.
 */
export default function UpdateModal({ convenio, updateList, closeModal }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const [data, setData] = useState();
  const [inRequest, setInRequest] = useState(false);
  const formikRef = useRef();

  const getConvenioData = async () => {
    setInRequest(true);
    const { data: result } = await RNHConvenioGet(credentials, { id: convenio.id });
    const encontrado = Array.isArray(result)
      ? result.find((c) => String(c.rnhConvenioId) === String(convenio.id))
      : null;
    setInRequest(false);

    if (!encontrado) {
      setSnackBarInfo({
        message: "No se pudo cargar el régimen horario seleccionado.",
        severity: "error",
        open: true,
      });
      closeModal();
      return;
    }

    formikRef.current.setValues({
      cod: encontrado.rnhConvenioCod ?? "",
      ley: encontrado.rnhConvenioLey ?? "",
      cargaSemanal: String(encontrado.rnhConvenioCargaSemanal ?? "").replace(/\D/g, ""),
    }, false);

    setData(encontrado);
  }

  const guardarConvenio = async (values) => {
    setInRequest(true);
    const response = await RNHConvenioUpd(credentials, {
      id: data?.rnhConvenioId ?? convenio.id,
      cod: values.cod,
      ley: values.ley,
      cargaSemanal: values.cargaSemanal,
      version: data?.rnhConvenioVersion ?? convenio.version,
    });

    setInRequest(false);
    if (response.ok) {
      setSnackBarInfo({
        message: "Régimen horario modificado correctamente",
        severity: "success",
        open: true,
      });
      
      formikRef.current.resetForm();
      updateList();
      closeModal();
    }
  };

  useEffect(() => {
    getConvenioData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        cod: "",
        ley: "",
        cargaSemanal: "",
      }}
      validationSchema={yup.object({
        cod: yup
          .string()
          .max(LARGO.cod, mensajeLargo(LARGO.cod))
          .required("Debe ingresar un código"),
        ley: yup
          .string()
          .oneOf(LEYES.map(({ cod }) => cod))
          .required("Debe seleccionar la ley"),
        cargaSemanal: yup
            .string()
            // \d* y no \d+ para que el vacio caiga en el required y no en este mensaje.
            .matches(/^\d*$/, "Solo números")
            .test(
                "rango",
                "Debe estar entre 0 y 168 hs",
                (valor) => !valor || Number(valor) <= 168
            )
            .required("Debe ingresar la carga semanal"),
      })}
      onSubmit={(values) => {
        guardarConvenio(values);
      }}
    >
      <Form className={styles.inputs} noValidate>
        <div className={styles["column"]}>
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            sx={{
              width: "100%",
              "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
            }}
          >
            <Grid item xs={4}>
              <FormikTextInput
                fullWidth={true}
                name="cod"
                labelText="Código"
                  inputProps={{ maxLength: LARGO.cod }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormikSelectInput
                fullWidth={true}
                name="ley"
                labelText="Ley"
              >
                <option value="" disabled>
                  Seleccione
                </option>
                {LEYES.map(({ cod, label }) => (
                  <option key={cod} value={cod}>
                    {label}
                  </option>
                ))}
              </FormikSelectInput>
            </Grid>
            <Grid item xs={4}>
              <FormikTextInput
                  fullWidth={true}
                  name="cargaSemanal"
                  labelText="Carga semanal"
                  inputProps={{ maxLength: 3, inputMode: "numeric" }}
                  InputProps={{
                      endAdornment: <InputAdornment position="end">hs</InputAdornment>,
                  }}
                  onChange={(e) =>
                      formikRef.current.setFieldValue(
                          "cargaSemanal",
                          e.target.value.replace(/\D/g, "")
                      )
                  }
              />
            </Grid>
          </Grid>
          <FormButton
            inRequest={inRequest}
            newFormData={{ button: "Modificar" }}
            onCloseModal={closeModal}
          />
          {(inRequest) && <SpinnerOverlay />}
        </div>
      </Form>
    </Formik>
  );
}
